import type { TFunction } from 'i18next';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError, type ApiFieldError } from 'utils/api-client';

/**
 * Map an `ApiError`'s `details` array to React Hook Form `setError` calls.
 *
 * Each `{ field, code, params? }` entry becomes a localized field-level error
 * via `messages.errors.<code>` (with i18next interpolation). Returns `true`
 * if any field-level errors were applied.
 *
 * Usage:
 *   const onSubmit = async (data) => {
 *     try { await submit(data); }
 *     catch (err) {
 *       const handled = applyApiDetailsToForm(err, setError, t);
 *       if (!handled) {
 *         setError('root.api', {
 *           type: 'manual',
 *           message: translateApiMessage(err, t, { fallback: t('feature.error_default') }),
 *         });
 *       }
 *     }
 *   };
 *
 * `fieldMap` lets you remap server field names to form field names when they
 * differ (e.g. server `password_confirmation` → form `password_confirmation`).
 */
export function applyApiDetailsToForm<TFieldValues extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<TFieldValues>,
  t: TFunction,
  options: { fieldMap?: Record<string, Path<TFieldValues>> } = {},
): boolean {
  if (!(err instanceof ApiError) || !err.details || err.details.length === 0) {
    return false;
  }

  const { fieldMap = {} } = options;
  let applied = false;

  for (const detail of err.details) {
    const message = translateFieldError(detail, t);
    if (!message) continue;

    const target = (fieldMap[detail.field] ?? detail.field) as Path<TFieldValues>;
    setError(target, { type: 'manual', message });
    applied = true;
  }

  return applied;
}

function translateFieldError(detail: ApiFieldError, t: TFunction): string {
  const key = `messages.errors.${detail.code}`;
  const interpolation = (detail.params ?? {}) as Record<string, unknown>;
  return t(key, {
    ...interpolation,
    defaultValue: t('messages.errors.GENERIC', { defaultValue: 'Invalid value.' }),
  });
}
